import * as types from '../types';
import { IRequestsApi } from '../contracts';
import { HttpApi } from './HttpApi';

const enc = encodeURIComponent;

/** Only the filters that are set go on the query string (the server rejects blanks). */
function params(query: Record<string, string | number | undefined> = {}): Record<string, string | number> {
  const result: Record<string, string | number> = {};
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== '') result[key] = value;
  });
  return result;
}

export class RequestsHttpApi extends HttpApi implements IRequestsApi {
  async listServiceRequests(query: types.ServiceRequestListQuery = {}): Promise<types.PortalPage<types.ServiceRequest>> {
    return (await this.http.get<types.APIResponse<types.PortalPage<types.ServiceRequest>>>('/service-requests', { params: params({ ...query }) })).data.data;
  }

  async getServiceRequest(id: string): Promise<types.ServiceRequest> {
    return (await this.http.get<types.APIResponse<types.ServiceRequest>>(`/service-requests/${enc(id)}`)).data.data;
  }

  async convertServiceRequest(id: string, data: types.ConvertServiceRequestInput = {}): Promise<types.ServiceRequest> {
    return (await this.http.post<types.APIResponse<types.ServiceRequest>>(`/service-requests/${enc(id)}/convert`, data)).data.data;
  }

  async declineServiceRequest(id: string, reason: string): Promise<types.ServiceRequest> {
    return (await this.http.post<types.APIResponse<types.ServiceRequest>>(`/service-requests/${enc(id)}/decline`, { reason })).data.data;
  }

  async listRescheduleRequests(query: types.RescheduleRequestListQuery = {}): Promise<types.PortalPage<types.RescheduleRequest>> {
    return (await this.http.get<types.APIResponse<types.PortalPage<types.RescheduleRequest>>>('/reschedule-requests', { params: params({ ...query }) })).data.data;
  }

  async approveRescheduleRequest(id: string, data: types.ApproveRescheduleInput = {}): Promise<types.RescheduleRequest> {
    return (await this.http.post<types.APIResponse<types.RescheduleRequest>>(`/reschedule-requests/${enc(id)}/approve`, data)).data.data;
  }

  async declineRescheduleRequest(id: string, message: string): Promise<types.RescheduleRequest> {
    return (await this.http.post<types.APIResponse<types.RescheduleRequest>>(`/reschedule-requests/${enc(id)}/decline`, { message })).data.data;
  }

  async listPortalAccessRequests(query: types.PortalAccessRequestListQuery = {}): Promise<types.PortalPage<types.PortalAccessRequest>> {
    return (await this.http.get<types.APIResponse<types.PortalPage<types.PortalAccessRequest>>>('/portal-access-requests', { params: params({ ...query }) })).data.data;
  }

  async decidePortalAccessRequest(id: string, decision: types.PortalAccessRequestDecision): Promise<types.PortalAccessRequest> {
    return (await this.http.patch<types.APIResponse<types.PortalAccessRequest>>(`/portal-access-requests/${enc(id)}`, decision)).data.data;
  }

  async listCustomerPortalLogins(customerId: string): Promise<types.CustomerPortalLogin[]> {
    return (await this.http.get<types.APIResponse<types.CustomerPortalLogin[]>>(`/customers/${enc(customerId)}/portal`)).data.data;
  }

  async inviteCustomerPortalUser(customerId: string, data: types.PortalInviteInput = {}): Promise<types.PortalInviteResult> {
    return (await this.http.post<types.APIResponse<types.PortalInviteResult>>(`/customers/${enc(customerId)}/portal/invite`, data)).data.data;
  }

  async resendCustomerPortalInvite(customerId: string, userId: string): Promise<types.PortalInviteResult> {
    return (await this.http.post<types.APIResponse<types.PortalInviteResult>>(`/customers/${enc(customerId)}/portal/resend`, { userId })).data.data;
  }

  async disableCustomerPortalUser(customerId: string, userId: string): Promise<types.CustomerPortalLogin[]> {
    return (await this.http.post<types.APIResponse<types.CustomerPortalLogin[]>>(`/customers/${enc(customerId)}/portal/disable`, { userId })).data.data;
  }

  async unlinkCustomerPortalUser(customerId: string, userId: string): Promise<types.CustomerPortalLogin[]> {
    return (await this.http.post<types.APIResponse<types.CustomerPortalLogin[]>>(`/customers/${enc(customerId)}/portal/unlink`, { userId })).data.data;
  }

  // ---- Web form intake (tracker item 49)

  async getWebFormIntake(): Promise<types.WebFormIntakeSettings> {
    return (await this.http.get<types.APIResponse<types.WebFormIntakeSettings>>('/service-request-intake')).data.data;
  }

  async updateWebFormIntake(enabled: boolean): Promise<types.WebFormIntakeSettings> {
    return (await this.http.put<types.APIResponse<types.WebFormIntakeSettings>>('/service-request-intake', { enabled })).data.data;
  }

  async regenerateWebFormKey(): Promise<types.WebFormIntakeSettings> {
    return (await this.http.post<types.APIResponse<types.WebFormIntakeSettings>>('/service-request-intake/regenerate-key', {})).data.data;
  }

  async getWebFormItems(key: string): Promise<types.WebFormItem[]> {
    return (await this.http.get<types.APIResponse<types.WebFormItem[]>>(`/public/web-form/${enc(key)}/items`)).data.data;
  }

  async submitWebFormRequest(key: string, data: types.WebFormRequestInput): Promise<void> {
    await this.http.post(`/public/web-form/${enc(key)}/service-requests`, data);
  }
}
